import { Form, Link, useActionData, useLoaderData, useNavigation, useRouteLoaderData, useSearchParams, type ActionFunctionArgs, type LoaderFunctionArgs, type ShouldRevalidateFunction } from "react-router";
import type { Amendment, ApprovalItem, ApprovalOutcome, ApprovalRules, Constraint, Decision, Retirement } from "@edm/contracts";
import { barLength, Lines, Loading, times } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { translate, useI18n } from "../../i18n/index.js";
import { isApiError } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Badge, Button, Callout, Card, FormError, LinkButton, PageHeader, ReviewIcon, Skeleton } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

/**
 * Reviews: the queue of write requests against memory itself (D-0016).
 *
 * Drafts hold `draft` records — nobody is waiting, they can pile up. Reviews hold
 * what someone put up for a ruling: a proposed decision, an amendment, a
 * retirement request. The engine assembles the queue, counts the votes, and
 * previews what approving retires with the same plan that applies it; this
 * page shows that and casts votes. It never judges a tally itself.
 */

// The generated item keeps embedded records as open objects; these are their shapes.
type Retiring = { constraint: Constraint; cause: string; replacedBy?: string };
const decisionOf = (it: ApprovalItem) => it.decision as unknown as Decision;
const amendmentOf = (it: ApprovalItem) => it.amendment as unknown as Amendment | undefined;
const retirementOf = (it: ApprovalItem) => it.retirement as unknown as Retirement | undefined;
const retiringOf = (it: ApprovalItem) => it.retiring as unknown as Retiring[];

export function reviewsLoader({ context }: LoaderFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  return { reviews: deferred(Promise.all([api.approvalQueue(), api.approvalRules()]).then(([queue, rules]) => ({ items: queue.items, rules }))) };
}

// Picking a tab or a request only changes the query, and the queue is already on the page:
// asking the engine again would hold the click for a round trip.
export const reviewsShouldRevalidate: ShouldRevalidateFunction = ({ currentUrl, nextUrl, formMethod, defaultShouldRevalidate }) =>
  !formMethod && currentUrl.pathname === nextUrl.pathname && currentUrl.search !== nextUrl.search ? false : defaultShouldRevalidate;

function afterVote(o: ApprovalOutcome) {
  const item = o.item as unknown as ApprovalItem;
  return { ok: o.applied ? translate("reviews.applied") : translate("reviews.pending", { have: item.approvals.length, required: item.required }) };
}

export async function reviewsAction({ context, request }: ActionFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  const form = await request.formData();
  const field = (k: string) => String(form.get(k) ?? "");
  const id = field("decisionId");
  const n = Number(field("number"));
  try {
    switch (`${field("intent")}:${field("kind")}`) {
      case "approve:decision":
        return afterVote(await api.approveDecision(id));
      case "approve:amendment":
        return afterVote(await api.approveAmendment(id, n));
      case "approve:retirement":
        return afterVote(await api.approveRetirement(id, n));
      case "decline:decision":
        await api.transitionDecision(id, "rejected");
        return { ok: translate("reviews.declined") };
      case "decline:amendment":
        await api.decideAmendment(id, n, false);
        return { ok: translate("reviews.declined") };
      case "decline:retirement":
        await api.closeRetirement(id, n, "decline");
        return { ok: translate("reviews.declined") };
      // Only the submitter may withdraw; the engine checks it (D-0016.A1).
      case "withdraw:decision":
        await api.withdrawDecision(id);
        return { ok: translate("reviews.withdrawn") };
      case "withdraw:amendment":
        await api.withdrawAmendment(id, n);
        return { ok: translate("reviews.withdrawn") };
      case "withdraw:retirement":
        await api.closeRetirement(id, n, "withdraw");
        return { ok: translate("reviews.withdrawn") };
    }
    return { error: translate("decision.unknownAction") };
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict") || isApiError(err, "forbidden")) return { error: err.message };
    throw err;
  }
}

const tone = (kind: ApprovalItem["kind"]) => (kind === "retirement" ? "danger" : kind === "amendment" ? "warning" : "info");

/** What approving retires — the reason this page exists: approving blind is how a blocking rule quietly stops blocking. */
function Cascade({ retiring }: { retiring: Retiring[] }) {
  const { t } = useI18n();
  if (retiring.length === 0) return <p className="dt-muted">{t("reviews.cascade.none")}</p>;
  return (
    <div className="dt-stack dt-gap-3">
      {retiring.map(({ constraint: c, cause, replacedBy }) => (
        <div key={c.id} className="dt-cascade__item" data-testid="retiring">
          <span className="dt-mono">{t("reviews.cascade.retires", { id: c.id })}</span>
          <span>{c.statement}</span>
          <span className="dt-inline dt-gap-2">
            <Badge tone={c.severity === "critical" || c.severity === "high" ? "danger" : "neutral"}>{c.severity}</Badge>
            <span className="dt-subtle">{replacedBy ? t("reviews.cascade.replacedBy", { id: replacedBy }) : cause}</span>
          </span>
        </div>
      ))}
      {retiring
        .filter((r) => r.constraint.enforcement === "blocking")
        .map((r) => (
          <Callout key={r.constraint.id} tone="danger">
            {t("reviews.cascade.blocking", { id: r.constraint.id })}
          </Callout>
        ))}
    </div>
  );
}

function RequestFields({ item, intent }: { item: ApprovalItem; intent: "approve" | "decline" | "withdraw" }) {
  const number = amendmentOf(item)?.number ?? retirementOf(item)?.number ?? 0;
  return (
    <>
      <input type="hidden" name="intent" value={intent} />
      <input type="hidden" name="kind" value={item.kind} />
      <input type="hidden" name="decisionId" value={item.decisionId} />
      <input type="hidden" name="number" value={number} />
    </>
  );
}

function RequestDetail({ item, rules, meId, base, busy }: { item: ApprovalItem; rules: ApprovalRules; meId: string; base: string; busy: boolean }) {
  const { t, formatDate } = useI18n();
  const d = decisionOf(item);
  const amendment = amendmentOf(item);
  const retirement = retirementOf(item);
  const retiring = retiringOf(item);
  const mine = item.submittedBy === meId;
  const blocked = mine && !rules.allowSelfApproval;
  const voted = item.approvals.some((v) => v.actorId === meId);

  return (
    <>
      <Card
        title={
          <span className="dt-inline dt-gap-2">
            <Badge tone={tone(item.kind)}>{t(`reviews.kind.${item.kind}`)}</Badge>
            <Link to={`${base}/decisions/${d.id}`} className="dt-mono">
              {item.ref}
            </Link>
            <Badge tone={item.approvals.length > 0 ? "info" : "neutral"}>{t("reviews.approvals", { have: item.approvals.length, required: item.required })}</Badge>
          </span>
        }
        description={item.submittedBy ? t("reviews.submitted", { actor: item.submittedBy, date: formatDate(item.submittedAt) }) : undefined}
        data-testid="request-detail"
      >
        <h3>{d.title}</h3>
        {amendment ? (
          <>
            <h4>{t("reviews.amend.title")}</h4>
            <div className="dt-amend">
              <Badge tone="success">{t("reviews.amend.added")}</Badge>
              <p>{amendment.summary}</p>
              {amendment.clarification && <p>{amendment.clarification}</p>}
              {amendment.addAssumptions?.map((a) => (
                <p key={a}>{a}</p>
              ))}
            </div>
            <p className="dt-subtle">{t("reviews.amend.note")}</p>
          </>
        ) : retirement ? (
          <>
            <h4>{t("reviews.retirement.reason")}</h4>
            <p>{retirement.reason}</p>
          </>
        ) : (
          <>
            {d.outcome && <p>{d.outcome}</p>}
            <h4>{t("reviews.create.rationale")}</h4>
            {d.rationale ? <p>{d.rationale}</p> : <Callout tone="warning">{t("reviews.create.noRationale")}</Callout>}
          </>
        )}
      </Card>

      {item.kind !== "amendment" && (
        <Card title={t("reviews.cascade.title")}>
          <Cascade retiring={retiring} />
        </Card>
      )}

      <div className="dt-inline dt-gap-2">
        <Form method="post">
          <RequestFields item={item} intent="approve" />
          <Button type="submit" variant={retiring.length > 0 ? "danger" : "primary"} loading={busy} disabled={blocked || voted}>
            {t(`reviews.approve.${item.kind}`)}
          </Button>
        </Form>
        <Form method="post">
          <RequestFields item={item} intent="decline" />
          <Button type="submit" variant="secondary" loading={busy}>
            {t("reviews.reject")}
          </Button>
        </Form>
        {mine && (
          <Form method="post">
            <RequestFields item={item} intent="withdraw" />
            <Button type="submit" variant="ghost" loading={busy}>
              {t("reviews.withdraw")}
            </Button>
          </Form>
        )}
      </div>
      {voted && <p className="dt-subtle">{t("reviews.voted")}</p>}
      {mine && <p className="dt-subtle">{t(blocked ? "reviews.selfApprovalBlocked" : "reviews.selfApproval")}</p>}
    </>
  );
}

/** The queue, then the selected request: what it is, what approving retires, the votes. */
export function ReviewsSkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <PageHeader
        title={t("reviews.title")}
        description={t("reviews.description")}
        actions={
          <span className="dt-inline dt-gap-1">
            <Skeleton shape="button-sm" width={96} />
            <Skeleton shape="button-sm" width={96} />
          </span>
        }
      />
      <Card padded={false}>
        <ul className="dt-requests">
          {times(3).map((i) => (
            <li key={i}>
              <div className="dt-requests__row">
                <Skeleton shape="badge" width={72} />
                <span className="dt-requests__title">
                  <Skeleton width={barLength(i)} />
                </span>
                <Skeleton width={96} />
              </div>
            </li>
          ))}
        </ul>
      </Card>
      <Card title={<Skeleton width={240} />} description={<Skeleton width={200} />}>
        <h3>
          <Skeleton width="50%" />
        </h3>
        <Lines count={3} />
      </Card>
      <Card title={t("reviews.cascade.title")}>
        <Lines count={1} />
      </Card>
      <div className="dt-inline dt-gap-2">
        <Skeleton shape="button" />
        <Skeleton shape="button" width={80} />
      </div>
    </Loading>
  );
}

export function ReviewsRoute() {
  const { reviews } = useLoaderData<typeof reviewsLoader>();
  // The tabs appear only once there is something to sort, so the header waits with the list.
  return (
    <Deferred data={reviews} fallback={<ReviewsSkeleton />}>
      {(data) => <Reviews {...data} />}
    </Deferred>
  );
}

function Reviews({ items, rules }: { items: ApprovalItem[]; rules: ApprovalRules }) {
  const result = useActionData() as { error?: string; ok?: string } | undefined;
  const busy = useNavigation().state === "submitting";
  const { me, workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const base = `/w/${workspace.slug}`;

  if (items.length === 0) {
    return (
      <>
        <PageHeader title={t("reviews.title")} description={t("reviews.description")} />
        {result?.ok && <Callout tone="success">{result.ok}</Callout>}
        <EmptyState title={t("reviews.empty.title")} hint={t("reviews.empty.hint")} icon={<ReviewIcon size={18} />} action={<LinkButton to={`${base}/drafts`}>{t("reviews.empty.drafts")}</LinkButton>} />
      </>
    );
  }

  const tab = params.get("tab") === "submitted" ? "submitted" : "mine";
  const visible = items.filter((it) => (tab === "submitted" ? it.submittedBy === me.actor.id : it.submittedBy !== me.actor.id));
  const selected = visible.find((it) => it.ref === params.get("request")) ?? visible[0];
  const select = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next);
  };

  return (
    <>
      <PageHeader
        title={t("reviews.title")}
        description={t("reviews.description")}
        actions={
          <span className="dt-inline dt-gap-1">
            {(["mine", "submitted"] as const).map((key) => (
              <Button key={key} size="sm" variant={tab === key ? "secondary" : "ghost"} onClick={() => select({ tab: key, request: null })}>
                {t(`reviews.tab.${key}`)}
              </Button>
            ))}
          </span>
        }
      />

      <FormError message={result?.error} />
      {result?.ok && <Callout tone="success">{result.ok}</Callout>}

      {visible.length === 0 ? (
        <EmptyState title={tab === "submitted" ? t("reviews.empty.submitted") : t("reviews.empty.title")} icon={<ReviewIcon size={18} />} />
      ) : (
        <>
          <Card padded={false}>
            <ul className="dt-requests">
              {visible.map((it) => (
                <li key={it.ref}>
                  <button type="button" className="dt-requests__row" aria-current={it.ref === selected?.ref} onClick={() => select({ request: it.ref })} data-testid="request">
                    <Badge tone={tone(it.kind)}>{t(`reviews.kind.${it.kind}`)}</Badge>
                    <span className="dt-requests__title">{amendmentOf(it)?.summary ?? decisionOf(it).title}</span>
                    {it.retiring.length > 0 && <Badge tone="warning">{t("reviews.cascade.warning", { count: it.retiring.length })}</Badge>}
                    <span className="dt-subtle">{t("reviews.approvals", { have: it.approvals.length, required: it.required })}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          {selected && <RequestDetail item={selected} rules={rules} meId={me.actor.id} base={base} busy={busy} />}
        </>
      )}
    </>
  );
}
